import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './FCOverview.css'



const IDENTITY_TAGS = [
    { label: 'Flight Controller', type: 'filled' },
    { label: 'Embedded Systems', type: 'outline' },
    { label: 'Real-Time Control', type: 'outline' },
    { label: 'STEM Education', type: 'outline' },
    { label: 'Indigenous Mfg.', type: 'dim' },
    { label: 'Beginner Safe', type: 'dim' },
    { label: 'RTF', type: 'dim' },
]

const KEY_POINTS = [
    'Optimised for real-time flight controller operation',
    '3-axis gyroscope + accelerometer onboard',
    'Integrated IMU for stable, autonomous hover',
    'No PID tuning required — ready to fly out of the box',
]

const WHO_FOR =
    'STEM students, hobbyists, and educators who need a reliable, low-friction platform for learning drone fundamentals. Also suits R&D teams prototyping light quadcopter builds on a 5V embedded stack.'

const META_BADGES = ['India', 'Aviatricks Aero Labs', 'v1.0']

export default function FCOverview() {
    const wrapRef = useRef(null)
    const eyebrowRef = useRef(null)
    const headingRef = useRef(null)
    const dividerRef = useRef(null)
    const leftColRef = useRef(null)
    const rightColRef = useRef(null)

    useEffect(() => {
        const ctx = gsap.context(() => {
            // eyebrow
            gsap.fromTo(eyebrowRef.current,
                { opacity: 0, y: 20 },
                {
                    opacity: 1, y: 0, duration: 0.7, ease: 'expo.out',
                    scrollTrigger: { trigger: eyebrowRef.current, start: 'top 88%', toggleActions: 'play none none none' },
                }
            )
            // heading
            gsap.fromTo(headingRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1, y: 0, duration: 1.0, ease: 'expo.out',
                    scrollTrigger: { trigger: headingRef.current, start: 'top 85%', toggleActions: 'play none none none' },
                }
            )
            // divider sweep
            gsap.fromTo(dividerRef.current,
                { scaleX: 0 },
                {
                    scaleX: 1, duration: 0.8, ease: 'expo.out', transformOrigin: 'left center',
                    scrollTrigger: { trigger: dividerRef.current, start: 'top 88%', toggleActions: 'play none none none' },
                }
            )
            // left col
            gsap.fromTo(leftColRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', delay: 0.1,
                    scrollTrigger: { trigger: leftColRef.current, start: 'top 86%', toggleActions: 'play none none none' },
                }
            )
            // right col
            gsap.fromTo(rightColRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', delay: 0.22,
                    scrollTrigger: { trigger: rightColRef.current, start: 'top 86%', toggleActions: 'play none none none' },
                }
            )
        }, wrapRef)
        return () => ctx.revert()
    }, [])

    return (
        <section ref={wrapRef} className="fco-wrap">
            <div className="fco-bg-grid" />

            <div className="fco-inner">
                {/* ── Section header ── */}
                <div className="fco-header">
                    <div ref={eyebrowRef} className="fco-eyebrow" style={{ opacity: 0 }}>
                        <span className="fco-dot" />FLIGHT CONTROLLER
                    </div>
                    <h2 ref={headingRef} className="fco-h2" style={{ opacity: 0 }}>
                        What is <em>MockFly?</em>
                    </h2>
                </div>

                {/* ── Two-column body ── */}
                <div className="fco-body">
                    {/* Left — intro + bullet points */}
                    <div ref={leftColRef} className="fco-col fco-col--left" style={{ opacity: 0 }}>
                        <div ref={dividerRef} className="fco-divider" />

                        <p className="fco-intro">
                            MockFly is Aviatricks' entry-level flight controller, engineered for
                            real-time flight control with an integrated IMU and beginner-safe tuning.
                            It ships ready-to-fly — no PID loops to configure, no guesswork. Designed
                            to make STEM education and first builds accessible without sacrificing the
                            fundamentals of embedded flight systems.
                        </p>

                        <ul className="fco-points">
                            {KEY_POINTS.map((pt, i) => (
                                <li key={i} className="fco-point">
                                    <span className="fco-point-bullet" />
                                    {pt}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Right — identity tags + who it's for + meta */}
                    <div ref={rightColRef} className="fco-col fco-col--right" style={{ opacity: 0 }}>
                        <p className="fco-col-label">// Identity</p>

                        <div className="fco-tags">
                            {IDENTITY_TAGS.map((t, i) => (
                                <span key={i} className={`fco-tag fco-tag--${t.type}`}>
                                    {t.label}
                                </span>
                            ))}
                        </div>

                        <div className="fco-who">
                            <p className="fco-col-label">// Who it's for</p>
                            <p className="fco-who-text">{WHO_FOR}</p>
                        </div>

                        <div className="fco-meta">
                            <span className="fco-meta-label">Made in</span>
                            {META_BADGES.map((b, i) => (
                                <span key={i} className="fco-meta-badge">{b}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}